import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

export const BusinessNameResultsRoot = superstyled({
  name: 'BusinessNameResults',
  slot: 'Root',
  tag: Box,
  styles: ({ theme }) => ({
    border: `1px solid ${theme.palette.border.main}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    height: '100%',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.grey.light,
    borderRadius: theme.shape.borderRadius
  })
});

export const NamesHeader = superstyled({
  name: 'BusinessNameResults',
  slot: 'NamesHeader',
  tag: Box,
  styles: () => ({
    flex: '1',
    display: 'flex',
    flexDirection: 'column'
  })
});

export const NamesContent = superstyled({
  name: 'BusinessNameResults',
  slot: 'NamesContent',
  tag: Box,
  styles: ({ theme }) => ({
    flex: '1',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2)
    }
  })
});

export const NamesSidebar = superstyled({
  name: 'BusinessNameResults',
  slot: 'NamesSidebar',
  tag: Box,
  styles: ({ theme }) => ({
    flex: '0 0 20rem',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      flex: 'none',
      minHeight: '200px'
    }
  })
});

export const MainContent = superstyled({
  name: 'BusinessNameResults',
  slot: 'MainContent',
  tag: Box,
  styles: ({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  })
});

export const NamesList = superstyled({
  name: 'BusinessNameResults',
  slot: 'NamesList',
  tag: Box,
  styles: () => ({
    overflow: 'auto'
  })
});

export const ButtonsContainer = superstyled({
  name: 'BusinessNameResults',
  slot: 'ButtonsContainer',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  })
});

export const ContentHeader = superstyled({
  name: 'BusinessNameResults',
  slot: 'ContentHeader',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(3, 3, 0, 3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5, 1.5, 0, 1.5)
    }
  })
});

export const ContentSubHeader = superstyled({
  name: 'BusinessNameResults',
  slot: 'ContentSubHeader',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      padding: theme.spacing(1, 0)
    }
  })
});

export const ContentTitle = superstyled({
  name: 'BusinessNameResults',
  slot: 'ContentTitle',
  tag: Text,
  styles: ({ theme }) => ({
    color: theme.palette.text.secondary
  })
});

export const ContentBody = superstyled({
  name: 'BusinessNameResults',
  slot: 'ContentBody',
  tag: Box,
  styles: ({ theme }) => ({
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5)
    }
  })
});

export const Section = superstyled({
  name: 'BusinessNameResults',
  slot: 'Section',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.border.main}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  })
});

export const SectionTitle = superstyled({
  name: 'BusinessNameResults',
  slot: 'SectionTitle',
  tag: Text,
  styles: ({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  })
});

export const SectionContent = superstyled({
  name: 'BusinessNameResults',
  slot: 'SectionContent',
  tag: Box,
  styles: ({ theme }) => ({
    color: theme.palette.text.primary,
    lineHeight: 1.6
  })
});

export const ConflictBadge = superstyled({
  name: 'BusinessNameResults',
  slot: 'ConflictBadge',
  tag: Box,
  propsNeeded: ['level'],
  styles: ({ theme, level }) => {
    const colorMap = {
      low: {
        background: theme.palette.success.light || '#e8f5e9',
        color: theme.palette.success.dark || '#2e7d32'
      },
      medium: {
        background: theme.palette.warning.light || '#fff3e0',
        color: theme.palette.warning.dark || '#e65100'
      },
      high: {
        background: theme.palette.error.light || '#ffebee',
        color: theme.palette.error.dark || '#c62828'
      }
    };
    const colors = colorMap[level] || colorMap.low;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1.5),
      borderRadius: theme.shape.borderRadius * 2,
      fontSize: '0.75rem',
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: 'capitalize',
      backgroundColor: colors.background,
      color: colors.color
    };
  }
});

export const DomainItem = superstyled({
  name: 'BusinessNameResults',
  slot: 'DomainItem',
  tag: Box,
  propsNeeded: ['available'],
  styles: ({ theme, available }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: available
      ? theme.palette.success.light || '#e8f5e9'
      : theme.palette.error.light || '#ffebee'
  })
});

export const DomainStatus = superstyled({
  name: 'BusinessNameResults',
  slot: 'DomainStatus',
  tag: Text,
  propsNeeded: ['available'],
  styles: ({ theme, available }) => ({
    fontSize: '0.75rem',
    fontWeight: theme.typography.fontWeightMedium,
    color: available
      ? theme.palette.success.dark || '#2e7d32'
      : theme.palette.error.dark || '#c62828'
  })
});

export const SearchLink = superstyled({
  name: 'BusinessNameResults',
  slot: 'SearchLink',
  tag: 'a',
  styles: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.info.main,
    textDecoration: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'underline'
    }
  })
});

export const SearchLinksContainer = superstyled({
  name: 'BusinessNameResults',
  slot: 'SearchLinksContainer',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  })
});
